import { CONSIGNMENT_SOFT_TONE_CLASS } from "@/core/helpers/consignmentTone";
import {
	ConsignmentCurationMethod,
	ConsignmentCurationRejectReason,
	ConsignmentCurationSampleMethod,
	ConsignmentCurationSampleStatus,
	ConsignmentCurationVerdict,
	ConsignmentRegistrationActorType,
	ConsignmentRegistrationRoutingReason,
	ConsignmentRegistrationStage,
} from "@/generated/graphql-consignment";

export const CASE_HISTORY_PREVIEW_LIMIT = 10;
// Labels fit 24 characters; longer names are allowed up to 100 but may be cut.
export const LABEL_NAME_PRINT_LENGTH = 24;
export const LABEL_NAME_MAX_LENGTH = 100;
export const REJECT_NOTE_MIN_LENGTH = 3;
export const MOVE_REASON_MIN_LENGTH = 5;
export const PHOTO_ONLY_REASON_MIN_LENGTH = 10;
export const COMMISSION_PCT_RANGE = { min: 1, max: 99 } as const;
export const BRANCH_SEARCH_MIN_COUNT = 6;
export const PUBLISH_PREVIEW_DEBOUNCE_MS = 400;
export const PERMIT_HINT_SHELF_LIFE_DAYS = 7;
export const SIDEBAR_BRANCH_PREVIEW_COUNT = 3;

export const CASE_TAB = {
	Curation: "curation",
	Onboarding: "onboarding",
	History: "history",
} as const;

export type CaseTab = (typeof CASE_TAB)[keyof typeof CASE_TAB];

export const CURATION_FILTER = {
	All: "all",
	AwaitingSample: "awaitingSample",
	Pending: "pending",
	Approved: "approved",
	Rejected: "rejected",
} as const;

export type CurationFilter =
	(typeof CURATION_FILTER)[keyof typeof CURATION_FILTER];

export const CURATION_FILTER_LABEL: Record<CurationFilter, string> = {
	all: "All",
	awaitingSample: "Awaiting sample",
	pending: "Pending",
	approved: "Approved",
	rejected: "Rejected",
};

export const CURATION_FILTER_EMPTY_TEXT: Record<CurationFilter, string> = {
	all: "No products in this view.",
	awaitingSample: "No sample is waiting.",
	pending: "Nothing pending. Every product has a decision.",
	approved: "No products in this view.",
	rejected: "No products in this view.",
};

export const CURATION_METHOD_LABEL: Record<ConsignmentCurationMethod, string> =
	{
		[ConsignmentCurationMethod.SampleRequired]: "Send sample",
		[ConsignmentCurationMethod.PhotoOnly]: "Photo + price list",
	};

// Shown on a sample-required product card until it is received (ONB-4.04 AC8)
export const SAMPLE_STATUS_LABEL: Partial<
	Record<ConsignmentCurationSampleStatus, string>
> = {
	[ConsignmentCurationSampleStatus.AwaitingSample]: "Awaiting sample",
	[ConsignmentCurationSampleStatus.InTransit]: "In transit",
};

export const SAMPLE_METHOD_LABEL: Record<
	ConsignmentCurationSampleMethod,
	string
> = {
	[ConsignmentCurationSampleMethod.Courier]: "Courier",
	[ConsignmentCurationSampleMethod.SelfDelivery]: "Self delivery",
};

export const VERDICT_LABEL: Record<ConsignmentCurationVerdict, string> = {
	[ConsignmentCurationVerdict.Approved]: "Approved",
	[ConsignmentCurationVerdict.Rejected]: "Rejected",
	[ConsignmentCurationVerdict.Pending]: "Pending",
};

export const VERDICT_BADGE_CLASS: Record<ConsignmentCurationVerdict, string> = {
	[ConsignmentCurationVerdict.Approved]: CONSIGNMENT_SOFT_TONE_CLASS.ok,
	[ConsignmentCurationVerdict.Rejected]: CONSIGNMENT_SOFT_TONE_CLASS.danger,
	[ConsignmentCurationVerdict.Pending]: CONSIGNMENT_SOFT_TONE_CLASS.neutral,
};

export const PRODUCT_REJECT_REASONS: {
	reason: ConsignmentCurationRejectReason;
	label: string;
}[] = [
	{
		reason: ConsignmentCurationRejectReason.Slow,
		label: "Unlikely to sell in the target stores",
	},
	{
		reason: ConsignmentCurationRejectReason.Overlap,
		label: "Similar product already in the target stores",
	},
	{
		reason: ConsignmentCurationRejectReason.Quality,
		label: "Quality or taste below standard",
	},
	{
		reason: ConsignmentCurationRejectReason.Label,
		label: "Packaging or label incomplete",
	},
	{
		reason: ConsignmentCurationRejectReason.Price,
		label: "Price not competitive",
	},
	{
		reason: ConsignmentCurationRejectReason.Legal,
		label: "Permit or legal document insufficient",
	},
	{
		reason: ConsignmentCurationRejectReason.Brand,
		label: "Not the brand owner (reseller, agent, or distributor)",
	},
	{
		reason: ConsignmentCurationRejectReason.Other,
		label: "Other (note required)",
	},
];

export const SAMPLE_NOT_RECEIVED_REASON = {
	reason: ConsignmentCurationRejectReason.SampleNotReceived,
	label: "Sample not received",
};

export const ROUTING_REASON_TEXT: Record<
	ConsignmentRegistrationRoutingReason,
	string
> = {
	[ConsignmentRegistrationRoutingReason.SingleTeamSelection]:
		"All chosen stores belong to this team.",
	[ConsignmentRegistrationRoutingReason.NearestStore]: "Nearest chosen store.",
	[ConsignmentRegistrationRoutingReason.TieLowestLoad]:
		"Nearest stores were tied; this team had the lowest load.",
	[ConsignmentRegistrationRoutingReason.TieRotation]:
		"Nearest stores and load were tied; this team had gone longest without a registration.",
};

export const ACTOR_TYPE_LABEL: Record<
	ConsignmentRegistrationActorType,
	string
> = {
	[ConsignmentRegistrationActorType.Admin]: "Admin",
	[ConsignmentRegistrationActorType.Vendor]: "Vendor",
	[ConsignmentRegistrationActorType.System]: "System",
};

// Storage codes from the registration form (BE oneof room chilled frozen)
const STORAGE_TYPE_LABEL: Record<string, string> = {
	room: "Room temperature",
	chilled: "Chilled",
	frozen: "Frozen",
};

export const formatStorageType = (storageType: string) =>
	STORAGE_TYPE_LABEL[storageType] ?? storageType;

// History action codes from the BE audit log (enum/consignment_audit_log.go)
export const HISTORY_ACTION_LABEL: Record<string, string> = {
	stage_change: "Stage changed",
	claim: "Claimed",
	assign: "Assigned",
	release: "Released to queue",
	take_over: "Taken over",
	auto_release: "Released automatically",
	move_team: "Moved to another team",
	curation_lock: "Curation method locked",
	pii_show: "Sensitive data shown",
	sample_receive: "Sample received",
	curation_verdict: "Product decision",
	curation_publish: "Result published",
	barcode_check: "Barcode checked",
	curation_store_decision: "Store decision",
	curation_method_switch: "Switched to photo only",
	sample_confirm: "Sample shipment confirmed",
	routing: "Routed",
	whatsapp_send: "WhatsApp sent",
};

// Unknown codes still read as words: "new_code" -> "New code"
export const formatHistoryAction = (action: string) =>
	HISTORY_ACTION_LABEL[action] ??
	action.charAt(0).toUpperCase() + action.slice(1).replaceAll("_", " ");

export const CLOSED_STAGES: ConsignmentRegistrationStage[] = [
	ConsignmentRegistrationStage.Closed,
	ConsignmentRegistrationStage.Spam,
	ConsignmentRegistrationStage.Active,
	ConsignmentRegistrationStage.RejectedFinal,
];

export const STEPPER_STEP = {
	Submitted: "Submitted",
	AwaitingSample: "Awaiting sample",
	SampleReceived: "Sample received",
	Curation: "Curation",
	ResultPublished: "Result published",
	Kyc: "KYC",
	Contract: "Contract",
	Active: "Active",
	Rejected: "Rejected",
} as const;

// Stage → stepper index.
export const STAGE_STEP_INDEX: Record<ConsignmentRegistrationStage, number> = {
	[ConsignmentRegistrationStage.Submitted]: 0,
	[ConsignmentRegistrationStage.Claimed]: 0,
	[ConsignmentRegistrationStage.AwaitingSample]: 1,
	[ConsignmentRegistrationStage.SampleSent]: 1,
	[ConsignmentRegistrationStage.InCuration]: 3,
	[ConsignmentRegistrationStage.AcceptedFinal]: 4,
	[ConsignmentRegistrationStage.RejectedFinal]: 4,
	[ConsignmentRegistrationStage.AwaitingAccountData]: 5,
	[ConsignmentRegistrationStage.AwaitingSignature]: 6,
	[ConsignmentRegistrationStage.AwaitingContractCheck]: 6,
	[ConsignmentRegistrationStage.ContractNeedsFix]: 6,
	[ConsignmentRegistrationStage.AwaitingActivation]: 7,
	[ConsignmentRegistrationStage.Active]: 7,
	[ConsignmentRegistrationStage.Closed]: 0,
	[ConsignmentRegistrationStage.Spam]: 0,
};

export const ENTITY_TYPE_LABEL: Record<string, string> = {
	perorangan: "Individual",
	badan: "Business entity",
};

export const BRAND_OWNERSHIP_LABEL: Record<string, string> = {
	owner: "Own production",
	not_owner: "Reseller / agent",
};
